import $, { type } from "jquery";
import CommonService from "./common.service";
import ScatterService from "./scatter.service";

class RanksService extends CommonService{
    /***  Series Functions ***/
    getSeries(name,gridIndex,xAxisIndex,yAxisIndex,config = {},isEmotionalNeeds = false){
        let baseSeries = {
            name: name,
            type: 'scatter',
            gridIndex: gridIndex,
            xAxisIndex: xAxisIndex,
            yAxisIndex: yAxisIndex,
            data: []
        };    
        $.extend(true, baseSeries, config);
        return baseSeries;
    }
    getNeedsMetricsGrid(){
        return [{
            left: 80,
            right: 220,
            top: 55,
            bottom: 135,
            containLabel: true
        }];
    }
    getAxisLabelRotation(names = []){
        return names.length > 70 ? -90 : -35;
    }
    getNeedsMetricsXAxis(names = [], tierBoundaryIndexes = []){
        let xAxis = this.getBaseXAxis();
        xAxis.data = names;
        $.extend(true, xAxis.axisLabel, this.getDefaultAxisTextStyleForAnalyticChart());
        $.extend(true, xAxis.axisLabel, {
            rotate: this.getAxisLabelRotation(names),
            hideOverlap: true,
            interval: 0
        });
        xAxis.splitLine = {
            show: true,
            lineStyle: {
                color: "#d9d9d9",
                type: "solid",
                width: 1
            },
            interval: function(index) {
                return index > 0 && (index % 5 == 0 || tierBoundaryIndexes.includes(index));
            }
        };
        return [xAxis];
    }
    getPrimaryNeedYAxis(name,axisConfiguration = {}){
        let minValue = typeof axisConfiguration.minValue != "undefined" && axisConfiguration.minValue != null ? axisConfiguration.minValue : 0;
        let maxValue = typeof axisConfiguration.maxValue != "undefined" && axisConfiguration.maxValue != null ? axisConfiguration.maxValue : null;
        if (typeof axisConfiguration.viewWindow != "undefined" && axisConfiguration.viewWindow != null) {
            minValue = typeof axisConfiguration.viewWindow.min != "undefined" && axisConfiguration.viewWindow.min != null ? axisConfiguration.viewWindow.min : minValue;
            maxValue = typeof axisConfiguration.viewWindow.max != "undefined" && axisConfiguration.viewWindow.max != null ? axisConfiguration.viewWindow.max : maxValue;
        }
        let yAxis = this.getYAxisForNeed(minValue, maxValue);
        yAxis.name = name;
        yAxis.inverse = typeof axisConfiguration.inverse != "undefined" ? axisConfiguration.inverse : false;
        yAxis.axisLabel.formatter = axisConfiguration.formatter || axisConfiguration.format || "{value}";
        yAxis.splitLine.show = true;
        return yAxis;
    }
    getSecondaryMetricYAxis(axisName = "Metric", axisConfiguration = {}){
        let minValue = typeof axisConfiguration.minValue != "undefined" && axisConfiguration.minValue != null ? axisConfiguration.minValue : null;
        let maxValue = typeof axisConfiguration.maxValue != "undefined" && axisConfiguration.maxValue != null ? axisConfiguration.maxValue : null;
        if (typeof axisConfiguration.viewWindow != "undefined" && axisConfiguration.viewWindow != null) {
            minValue = typeof axisConfiguration.viewWindow.min != "undefined" && axisConfiguration.viewWindow.min != null ? axisConfiguration.viewWindow.min : minValue;
            maxValue = typeof axisConfiguration.viewWindow.max != "undefined" && axisConfiguration.viewWindow.max != null ? axisConfiguration.viewWindow.max : maxValue;
        }
        let yAxis = this.getYAxisForNeed(minValue, maxValue);
        yAxis.name = axisName;
        yAxis.position = "right";
        yAxis.inverse = typeof axisConfiguration.inverse != "undefined" ? axisConfiguration.inverse : false;
        yAxis.axisLabel.formatter = axisConfiguration.formatter || axisConfiguration.format || "{value}";
        yAxis.splitLine.show = false;
        return yAxis;
    }
    getAverageMarkLine(value, color, name = "Average"){
        if (value === null || typeof value == "undefined" || isNaN(value)) {
            return {
                symbol: "none",
                label: { show: false },
                data: []
            };
        }
        return {
            symbol: "none",
            label: {
                show: false,
                color: color
            },
            lineStyle: {
                color: color,
                type: "dotted",
                width: 2
            },
            data: [{ yAxis: Number(value) }]
        };
    }
    getAverageLineSeries(name, averageValue, color, yAxisIndex = 0, numberOfValues = 0){
        if (averageValue === null || typeof averageValue == "undefined" || isNaN(averageValue)) {
            return null;
        }
        return {
            name: name,
            type: "line",
            xAxisIndex: 0,
            yAxisIndex: yAxisIndex,
            gridIndex: 0,
            symbol: "none",
            silent: true,
            lineStyle: {
                color: color,
                type: "dotted",
                width: 2
            },
            label: {
                show: false
            },
            data: Array(numberOfValues).fill(Number(averageValue))
        };
    }
    getZeroPercentMarkLine(values = []){
        let numericValues = values.filter(value => !isNaN(value)).map(value => Number(value));
        let hasPositive = numericValues.some(value => value > 0);
        let hasNegative = numericValues.some(value => value < 0);
        if (!hasPositive || !hasNegative) {
            return {};
        }
        return {
            data: [{
                yAxis: 0,
                lineStyle: {
                    color: "#333333",
                    type: "solid",
                    width: 2
                },
                label: {
                    show: true,
                    formatter: "0%"
                }
            }]
        };
    }
    getNeedCircleSeries(name, values = [], averageValue = null, averageColor = "#2F5EB3"){
        return {
            name: name,
            type: "scatter",
            xAxisIndex: 0,
            yAxisIndex: 0,
            gridIndex: 0,
            symbol: "circle",
            symbolSize: 13,
            itemStyle: {
                color: averageColor
            },
            data: values
        };
    }
    formatNeedsMetricsRankValue(seriesName, valueToDisplay, seriesIndex = 0){
        if (seriesName.endsWith(" Avg")) {
            return Number(valueToDisplay).toFixed(2);
        } else if (seriesName == "Value Gap" || seriesName == "Satisfaction Gap Size" || seriesName == "Emotional Gap Size" || seriesName == "Importance Average") {
            return Number(valueToDisplay).toFixed(3);
        } else if (seriesName == "n=") {
            return Number(valueToDisplay).toFixed(0);
        } else if (seriesName == "Willingness by Need") {
            return Number(valueToDisplay).toFixed(2) + "%";
        }
        let suffix = seriesIndex == 1 && !seriesName.includes("Growth") ? "" : "%";
        return Number(valueToDisplay) + suffix;
    }
    getMetricBarSeries(name, values = [], averageValue = null, averageColor = "#c8c1aa"){
        let vm = this;
        return {
            name: name,
            type: "bar",
            xAxisIndex: 0,
            yAxisIndex: 1,
            gridIndex: 0,
            barMaxWidth: 28,
            label: {
                align: "center",
                offset: [7,-13],
                fontFamily: "Franklin Gothic Medium Cond",
                fontSize: 11,
                show: true,
                position: "insideBottom",
                rotate: 90,
                formatter: function(params) {
                    return !isNaN(params.value) ? vm.formatNeedsMetricsRankValue(params.seriesName, params.value, 1) : "";
                }
            },
            itemStyle: {
                color: averageColor
            },
            markLine: {
                symbol: "none",
                label: { show: false },
                data: []
            },
            data: values
        };
    }
    /*** yAxis Functions ***/
    getMarkAreaData(){
        return [
            [
              {
                  itemStyle: {
                  color: "#f2dcdb"
                  }
              },            
              {
                  yAxis: 10
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#FFFFC5"
                  },
                  yAxis: 10
              },            
              {
                  yAxis: 35
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#FAFBF7"
                  },
                  yAxis: 35
              },            
              {
                  yAxis: 75
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#EBF1DE"
                  },
                  yAxis: 75
              },            
              {
                  yAxis: 90
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#D8E4BC"
                  },
                  yAxis: 90
              },            
              {
                  yAxis: 100
              }
            ]
        ];
      }
  
/*Tooltip Functions */
toolTipForRanksChart = (params) => {
    let returnValue = "";
    for (let i = 0; i < params.length; ++i) {
        let valueToDisplay = params[i].value;
        let seriesName = params[i].seriesName;
        //returnValue += params[i].marker += " ";
        returnValue += params[i].seriesName += " ";
        returnValue += "<span style='text-align:right'><b>" 
        if (valueToDisplay > 0){
           returnValue += Number(valueToDisplay).toFixed(1) + "%";
        } else {
            returnValue += "--";
        }
        returnValue += "</b></span>";
        returnValue += "<br>";
    }
    return returnValue;
  }
toolTipForNeedsMetricsRankChart = (params) => {
    let returnValue = "";
    if (params.length > 0) {
        returnValue += "<b>" + params[0].axisValue + "</b><br>";
    }
    for (let i = 0; i < params.length; ++i) {
        let valueToDisplay = params[i].value;
        returnValue += params[i].marker + " " + params[i].seriesName + " ";
        returnValue += "<span style='text-align:right'><b>";
        if (!isNaN(valueToDisplay)) {
            returnValue += this.formatNeedsMetricsRankValue(params[i].seriesName, valueToDisplay, i);
        } else {
            returnValue += "--";
        }
        returnValue += "</b></span><br>";
    }
    return returnValue;
  }
}
export default new RanksService();             
